import Button from '@atoms/Button/Button';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import IExternalVideo from '@models/IExternalVideo';
import { ILink } from '@models/ILink';
import { IMeta } from '@models/IMeta';
import ExternalVideo from '@molecules/ExternalVideo/ExternalVideo';
import useCookieStatusStore from '@state/CookieStatusStore';
import updateDataLayer from '@utils/helpers/gtm';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import useIsClientSide from '@utils/hooks/useIsClientSide';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import DesktopVideoList from './videoLists/DesktopVideoList';
import MobileVideoList from './videoLists/MobileVideoList';

interface Props {
  readonly title: string;
  readonly link?: ILink;
  readonly videoPlaylist: IExternalVideo[];
  readonly loadMoreText: string;
  readonly anchorId: string;
  readonly meta?: IMeta;
}

const ExternalVideoPlaylist = ({
  title,
  link,
  videoPlaylist,
  loadMoreText,
  anchorId = '',
  meta,
}: Props) => {
  const isMobileView = useMediaQuery(1440);
  const [activeSource, setActiveSource] = useState(
    `${videoPlaylist[0]?.videoId ?? ''}`
  );
  const [activeSourceIndex, setActiveSourceIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const isClientSide = useIsClientSide();
  const { cookieStatus } = useCookieStatusStore((state) => ({
    cookieStatus: state.cookieStatus,
  }));
  const { playVideoAriaLabel } = useAriaLabels();

  return videoPlaylist.length > 0 ? (
    <section
      id={`a-${anchorId}`}
      className="component-padding dark:bg-black-100 dark:text-white"
      data-epi-block-id={meta?.contentLink}
      data-testid="CT-10-B-Video-Embed-Playlist"
    >
      <div className="container grid grid-cols-12">
        <div className="col-span-12 xl:col-span-10 xl:col-start-2 xl:col-end-12">
          {(title || (link?.url && link?.name)) && (
            <div
              className={classNames(
                'flew-row mb-10 flex flex-wrap items-center gap-5',
                title ? 'justify-between' : 'justify-end'
              )}
            >
              {title && (
                <h2
                  className="h3 mr-auto"
                  data-epi-property-name={meta?.editHints?.text}
                  data-testid="CT-10-B-Video-Embed-Playlist-Title"
                >
                  {title}
                </h2>
              )}
              {link?.name && link?.url && (
                <Button
                  link={link}
                  editHint={`${meta?.editHints?.link}`}
                  linkCallback={() => {
                    updateDataLayer({
                      event: 'ctaClicks',
                      content_type: link.name,
                    });
                  }}
                  dataTestId="CT-10-B-Video-Embed-Playlist-Link-Button"
                />
              )}
            </div>
          )}
          <div
            className="flex w-full flex-col xl:flex-row"
            data-epi-property-name={meta?.editHints?.videoPlaylist}
            data-testid="CT-10-B-Video-Embed-Playlist-Container"
          >
            <div className="relative aspect-video w-full hover:cursor-pointer">
              {/* cookieStatus will only be true on the client side and it might happen that on the initial render,
                  when cookie banner is not displayed, cookieStatus is false on the server and true on the client,
                  resulting in a hydration error. Thus we want to make sure the video component is only rendered
                  on the client. */}
              {cookieStatus && isClientSide && (
                <ExternalVideo
                  videoProvider={
                    videoPlaylist[activeSourceIndex]?.videoProvider
                  }
                  videoId={activeSource}
                  title={videoPlaylist[activeSourceIndex]?.title}
                  autoPlay={
                    videoPlaylist.length === activeSourceIndex ? false : playing
                  }
                  isInvisible={
                    videoPlaylist.length === activeSourceIndex || !playing
                  }
                  onEndedVideo={() => {
                    const nextVideoIndex = activeSourceIndex + 1;
                    if (videoPlaylist[nextVideoIndex]) {
                      const { videoId } = videoPlaylist[nextVideoIndex]!;
                      setActiveSource(`${videoId}`);
                      setActiveSourceIndex(nextVideoIndex);
                      setPlaying(true);
                    } else {
                      const { videoId } = videoPlaylist[0]!;
                      setActiveSource(`${videoId}`);
                      setActiveSourceIndex(0);
                      setPlaying(false);
                    }
                  }}
                  dataTestId="CT-10-B-Video-Embed-Playlist-External-Video"
                />
              )}

              <AnimatePresence>
                {!playing && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => {
                      if (cookieStatus && videoPlaylist[activeSourceIndex]) {
                        const { videoId } = videoPlaylist[activeSourceIndex]!;
                        setActiveSource(`${videoId}`);
                        setPlaying(true);
                      }
                    }}
                    className="-bottom-[0.1rem] absolute top-0 left-0 flex w-full items-center justify-center"
                  >
                    {!playing &&
                      videoPlaylist[activeSourceIndex]?.coverImage && (
                        <>
                          <Image
                            image={
                              videoPlaylist[activeSourceIndex]!.coverImage!
                            }
                            classes="w-full"
                            imageClasses="h-full w-full aspect-video"
                            dataTestId="CT-10-B-Video-Embed-Playlist-Active-Video-Cover-Image"
                          />
                          <div className="absolute bottom-0 hidden h-1/3 w-full bg-gradient-to-t from-black-100 to-transparent opacity-60 xl:block" />
                        </>
                      )}
                    <div className="absolute bottom-2.5 left-2.5 flex items-center md:bottom-5 md:left-5">
                      <button
                        type="button"
                        aria-label={playVideoAriaLabel}
                        className=""
                        onClick={() => {
                          if (
                            cookieStatus &&
                            videoPlaylist[activeSourceIndex]
                          ) {
                            const { videoId } =
                              videoPlaylist[activeSourceIndex]!;
                            setActiveSource(`${videoId}`);
                            setPlaying(true);
                          }
                        }}
                        data-testid="CT-10-B-Video-Embed-Playlist-External-Video-Play-Button"
                      >
                        <div className="h-[2.1875rem] w-[2.1875rem] md:h-[2.875rem] md:w-[2.875rem]">
                          <Icon name="play_icon_circle" size="100%" />
                        </div>
                      </button>
                      {videoPlaylist[activeSourceIndex]?.title && (
                        <p
                          className="h6 hidden text-white xl:block xl:pl-5"
                          data-testid="CT-10-B-Video-Embed-Playlist-External-Video-Active-Video-Title"
                        >
                          {videoPlaylist[activeSourceIndex]!.title}
                        </p>
                      )}
                    </div>
                    {videoPlaylist[activeSourceIndex]?.label && (
                      <span
                        className="body-one absolute top-2 left-2 rounded-[6rem] bg-white pt-2 pr-3 pb-2 pl-3 text-center opacity-70 xl:top-5 xl:left-5 dark:bg-black-100"
                        data-testid="CT-10-B-Video-Embed-Playlist-External-Video-Active-Video-Label"
                      >
                        {videoPlaylist[activeSourceIndex]!.label}
                      </span>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {isMobileView ? (
              <MobileVideoList
                videoPlaylist={videoPlaylist}
                setActiveSource={setActiveSource}
                setActiveSourceIndex={setActiveSourceIndex}
                activeSourceIndex={activeSourceIndex}
                setPlaying={setPlaying}
                loadMoreText={loadMoreText}
              />
            ) : (
              <DesktopVideoList
                videoPlaylist={videoPlaylist}
                setActiveSource={setActiveSource}
                setActiveSourceIndex={setActiveSourceIndex}
                setPlaying={setPlaying}
                playlistId={meta?.contentLink ? `${meta.contentLink}` : ''}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  ) : null;
};

export default ExternalVideoPlaylist;
